import { useMemo, useState } from 'react'
import { useIsPortrait } from '../../../util/breakpoints'
import styled from '@emotion/styled'
import { useInView } from 'react-intersection-observer'
import { VideoPlayer } from '../../video/VideoPlayer'
import { getFirstFrameUrl } from '../../../util/path'
import { useMuteState } from '../../../util/muteState'
import { VIEWPORT_HEIGHT } from '../../../util/viewportHeight'

export interface Video {
  videoId: string
  videoLandscape: string
  videoPortrait: string
  scrollPercentage: number
  alt: string
  playing: boolean
  type: 'loop' | 'play_once' | 'play_once_with_controls'
  showMuteButton: boolean
  lang: string
}

export default function Video(props: Video) {
  const [videoBuffered, setVideoBuffered] = useState(false)
  const isPortrait = useIsPortrait()
  const { muted, setMuted } = useMuteState()

  const { ref, inView: videoMostlyInView } = useInView({
    threshold: 0.8,
  })

  const posterUrl = isPortrait
    ? getFirstFrameUrl(props.videoPortrait)
    : getFirstFrameUrl(props.videoLandscape)

  const memoizedPlayer = useMemo(() => {
    const videoUrl = isPortrait ? props.videoPortrait : props.videoLandscape

    let isMuted = true
    if (props.type === 'play_once_with_controls') {
      isMuted = muted
    }

    const videoIsPlaying = () => {
      if (props.playing && videoMostlyInView) {
        return true
      }

      return false
    }

    const getVideoControls = (): 'none' | 'browserDefault' | 'customWithoutMuteButton' | 'customWithMuteButton' => {
      if (props.type === 'play_once_with_controls') {
        if (props.showMuteButton) {
          return 'customWithMuteButton'
        } else {
          return 'customWithoutMuteButton'
        }
      }

      return 'none'
    }

    return (
      <VideoPlayer
        videoUrl={videoUrl}
        onBufferEnd={() => setVideoBuffered(true)}
        playing={videoIsPlaying()}
        controls={getVideoControls()}
        loop={props.type === 'loop'}
        subtitleLang={props.lang}
        muted={isMuted}
        fullscreen={true}
        alt={props.alt}
        aria-label={props.alt}
        onMuteChange={setMuted}
      />
    )
  }, [
    videoMostlyInView,
    props.videoPortrait,
    props.videoLandscape,
    props.playing,
    props.type,
    isPortrait,
    props.alt,
    props.lang,
    props.showMuteButton,
    muted,
    setMuted,
  ])

  return (
    <>
      <VideoContainer ref={ref}>
        <Poster posterIsVisible={!videoBuffered} posterUrl={posterUrl} />
        {(props.playing || videoBuffered) && memoizedPlayer}
      </VideoContainer>
    </>
  )
}

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${VIEWPORT_HEIGHT};
`

interface PosterProps {
  posterIsVisible: boolean
  posterUrl: string
}
const Poster = styled.div<PosterProps>`
  position: absolute;
  opacity: ${(props) => (props.posterIsVisible ? '1' : '0')};
  transition: opacity 0.5s ease-in-out;
  pointer-events: none;
  z-index: 3;
  height: ${VIEWPORT_HEIGHT};
  width: 100%;
  background-image: url(${(props) => props.posterUrl});
  background-position: center;
  background-size: cover;
`
